import React from "react";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { BRAND, BRAND_DARK, EMPTY_DISC } from "../chartTheme";

/** What a figure the backend has not counted shows in place of a number. */
const NO_FIGURE = "-";

/** The card's own footprint, so the block standing in for it is the size of what
 *  lands in its place: the heading, the figure, the meter and the two labels. */
const FOOTPRINT = "h-[168px] rounded-card";
const FLOOR = "min-h-[168px]";

/** How tall the meter is, and how much of the surface stands between its two
 *  segments — the 2px gap every touching pair of fills in this app leaves, so
 *  the two never bleed into one bar. */
const METER_HEIGHT = 10;
const SEGMENT_GAP = 2;

/** `25.0` reads as "25%"; a rate that is not whole keeps one decimal. */
const formatRate = (rate: number) =>
  `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`;

interface MobileConversionCardProps {
  /** Clients with a plan running — the converted share of the requests below.
   *  `null` where the backend sent no figure, which reads as a dash rather than
   *  a zero. */
  paidClients: number | null;
  /** Every payment request ever raised, which is the whole the meter divides. */
  totalRequests: number | null;
  /** What share of those requests converted, as the backend worked it out —
   *  `25.0` for a quarter. Its own figure rather than the two above divided
   *  here: the backend counts requests this app never lists. */
  conversionRate: number | null;
  /** Whether anyone has been asked for money yet, which is what puts the card
   *  into the state a new workspace opens it in. */
  empty?: boolean;
  /** Where the dashboard call is. */
  phase?: LoadPhase;
}

/** One end of the meter, written out: a rule in the segment's own colour, the
 *  count, and what it counts. Direct labels rather than a legend box — there are
 *  two of them, and a box would be a third thing to read. */
const MeterLabel: React.FC<{
  color: string;
  value: React.ReactNode;
  label: string;
}> = ({ color, value, label }) => (
  <li className="flex items-center gap-2 min-w-0">
    <span
      aria-hidden
      className="w-[3px] h-7 rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
    <span className="min-w-0">
      <span className="block text-[14px] font-semibold leading-tight text-ink">
        {value}
      </span>
      <span className="block text-[12px] leading-tight text-ink-muted">
        {label}
      </span>
    </span>
  </li>
);

/**
 * How many of the requests raised turned into paying clients, as a phone reads
 * it.
 *
 * Not `TotalClientsCard` at a narrower width. That card answers the question
 * with a two-slice pie, and a two-slice pie is a bar that has been bent into a
 * circle: it costs a 140px square to say one thing a 10px strip says better, and
 * on a phone that square is a fifth of the fold. A part-to-whole split of two is
 * a meter — the rate as the figure, the split under it, and the two counts
 * written out beside their own colours.
 *
 * So this is the same three figures the wide screen shows, in the form they read
 * best in at 360px: the share as the card's lead, because that is the number
 * anybody glancing at a dashboard is after, and the counts it was worked out
 * from underneath it. The pie is still what a pointer gets — it has the room for
 * it, and a hover to name its slices with.
 *
 * Which of the two renders is `DashboardPage`'s decision — see `useIsMobile`.
 */
const MobileConversionCard: React.FC<MobileConversionCardProps> = ({
  paidClients,
  totalRequests,
  conversionRate,
  empty = false,
  phase = "ready",
}) => {
  // Paid clients are a subset of the requests raised, so the meter splits the
  // requests into converted and not — not the two headline figures against each
  // other. Clamped at zero: a backend counting the two differently must not
  // draw a negative segment.
  const paid = Math.max(0, paidClients ?? 0);
  const requests = Math.max(0, totalRequests ?? 0);
  const unconverted = Math.max(0, requests - paid);

  /**
   * Whether the meter has anything to divide.
   *
   * The same caution `TotalClientsCard` takes with its disc: zero paid against
   * zero requests is not a split of anything, and it happens well after the
   * first run — a workspace that has taken money but has nobody on a plan right
   * now counts both at zero while the earnings card above it still plots a year.
   * So the empty track stands in whenever there is no split, not only on a
   * workspace that has not started.
   */
  const showsEmptyMeter = empty || paid + unconverted <= 0;

  /** How much of the meter the converted share takes. Off the counts rather than
   *  off `conversionRate`: the rate is the backend's own figure over requests
   *  this app never lists, and a fill that did not match the two counts beside
   *  it would be the card disagreeing with itself. */
  const paidShare = showsEmptyMeter ? 0 : (paid / (paid + unconverted)) * 100;

  if (phase !== "ready") {
    return <SkeletonCard phase={phase} className={FOOTPRINT} />;
  }

  return (
    <section
      className={`card-surface border border-line bg-white p-4 ${FLOOR}`}
    >
      <h2 className="text-[15px] font-semibold text-ink">Total clients</h2>

      {/* The rate is the card's lead, and the sentence under it is what keeps a
          bare percentage from reading as a change since last month. Where there
          is no split there is no share of anything to report, whatever rate the
          backend worked out — so the line says what the card is waiting for
          instead. */}
      {showsEmptyMeter || conversionRate === null ? (
        <p className="mt-1 text-[13px] text-ink-muted">
          {empty
            ? "No requests raised yet."
            : "Nobody is on a plan right now."}
        </p>
      ) : (
        <p className="mt-1 flex items-baseline gap-1.5">
          <span className="text-[26px] font-semibold leading-none tracking-tight text-ink">
            {formatRate(conversionRate)}
          </span>
          <span className="text-[12px] text-ink-muted">
            of {requests} requests became clients
          </span>
        </p>
      )}

      {/* The split. The track is a lighter step of the same brand ramp as the
          fills rather than a grey, so an empty meter still reads as the same
          measurement waiting to be taken. A 2px gap of the surface colour
          between the two segments, which is what stops them reading as one bar
          with a colour change in it. */}
      <div
        role="img"
        aria-label={
          showsEmptyMeter
            ? "No requests to convert yet"
            : `${paid} of ${requests} requests converted`
        }
        className="mt-4 flex overflow-hidden rounded-full"
        style={{ height: METER_HEIGHT, backgroundColor: EMPTY_DISC }}
      >
        {!showsEmptyMeter && (
          <>
            <span
              className="h-full rounded-full"
              style={{
                width: `${paidShare}%`,
                backgroundColor: BRAND,
                // The gap, drawn as the surface colour beside the fill rather
                // than as a margin — a margin on a percentage width would come
                // off the measurement itself.
                boxShadow: `${SEGMENT_GAP}px 0 0 #FFFFFF`,
              }}
            />
            <span
              className="h-full flex-1 rounded-full"
              style={{
                marginLeft: SEGMENT_GAP,
                backgroundColor: BRAND_DARK,
              }}
            />
          </>
        )}
      </div>

      {/* The converted share beside its own colour, and the whole it came out of
          beside the rest of the meter. The unconverted count is the one figure
          of the three this card does not write out — it is the gap between the
          two that are here, and the meter already draws it. Each reads as the
          dash the rest of the dashboard uses where the backend sent no figure. */}
      <ul className="mt-3.5 flex items-center justify-between gap-3">
        <MeterLabel
          color={BRAND}
          value={empty ? 0 : paidClients ?? NO_FIGURE}
          label="Paid clients"
        />
        <MeterLabel
          color={BRAND_DARK}
          value={empty ? 0 : totalRequests ?? NO_FIGURE}
          label="Total requests"
        />
      </ul>
    </section>
  );
};

export default MobileConversionCard;
